import React, { useEffect, useRef } from 'react';
import SurveyForm from './SurveyForm';
import { useSurvey } from '../redux/hooks/useSurvey';
import useCheckIn from '../redux/hooks/useCheckIn';
/**
 * Component for rendering Non-LHC forms using a JSON schema.
 * Uses Redux for state management and persistence.
 *
 * @param {Object} props - Component props
 * @param {Object} props.formDetail - Details about the form, including schema and formData.
 * @param {Function} props.onSubmit - Callback function to call on form submission.
 * @returns {JSX.Element} The Non-LHC form renderer component.
 */
const NonLhcFormRenderer = ({ formDetail, onSubmit }) => {
  
  // Get check-in state and actions from Redux
  const {
    currentStep,
    updateCurrentStep,
    currentFormId,
    currentFormVersion,
    updateCurrentFormId,
    updateCurrentFormVersion
  } = useCheckIn();
  
  // Get survey data and actions from Redux
  const {
    formResponses,
    updateSurveyData,
    updateCurrentFormId: updateSurveyFormId,
    submitFormResponse,
    updateResponse
  } = useSurvey();
  
  // Extract form ID and version from formDetail
  const formId = formDetail?.form_id;
  const formVersion = formDetail?.version || formDetail?.original_version;
  
  // Use refs to track initialization state
  const isInitialized = useRef(false);
  const schemaRef = useRef(null);
  
  // Initialize form in Redux only once when component mounts
  useEffect(() => {
    if (formId && !isInitialized.current) {
      // Store the current form ID and version in checkIn slice
      updateCurrentFormId(formId);
      updateCurrentFormVersion(formVersion);
      
      // Also store in survey slice for compatibility
      updateSurveyFormId(formId);
      
      isInitialized.current = true;
    }
  }, [formId, formVersion, updateCurrentFormId, updateCurrentFormVersion, updateSurveyFormId]);
  
  // Store form schema and details only when schema changes
  useEffect(() => {
    // Skip if no formId or no schema
    if (!formId || !formDetail?.schema) return;
    
    // Skip if schema hasn't changed
    const currentSchemaStr = JSON.stringify(formDetail.schema);
    if (schemaRef.current === currentSchemaStr) return;
    
    // Update schema reference
    schemaRef.current = currentSchemaStr;
    
    // Store the form schema and details
    updateSurveyData({
      [formId]: {
        schema: formDetail.schema,
        metadata: {
          title: formDetail.title || '',
          description: formDetail.description || '',
          lastUpdated: new Date().toISOString()
        }
      }
    });
  }, [formId, formDetail, updateSurveyData]);
  
  // Get saved responses for this form - prioritize backend data if available
  const backendFormData = formDetail?.formData || {};
  const reduxFormData = formId ? formResponses[formId] || {} : {};
  
  // Merge backend data with Redux data, giving priority to backend data
  const savedResponses = { ...reduxFormData, ...backendFormData };
  
  // Handle form submission
  const handleSubmit = (data) => {
    console.log('NonLhcFormRenderer: Form submitted with data:', data);
    if (formId) {
      // Save the form responses to Redux
      submitFormResponse(formId, data);
      
      // Increment the step count in Redux
      updateCurrentStep(currentStep + 1);
      
      // Call the parent onSubmit callback
      onSubmit(data);
    }
  };
  
  // Handle field change
  const handleFieldChange = (fieldKey, value) => {
    if (formId) {
      updateResponse(formId, fieldKey, value);
    }
  };
  
  // Render the form using SurveyForm component
  return (
    <div className="non-lhc-form-container">
      {/* Form Header with Navigation Info */}
      <div className="form-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">Form {formDetail.form_id}</h4>
            <small className="text-muted">Version {formDetail.original_version || formDetail.version}</small>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm me-2"
              onClick={() => window.history.back()}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Survey Form */}
      <SurveyForm
        json={formDetail.schema}
        initialData={savedResponses}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default NonLhcFormRenderer;